import { Component, ChangeDetectionStrategy, inject } from "@angular/core";
import { CommonModule } from "@angular/common";
import { ReactiveFormsModule } from "@angular/forms";
import { TranslateModule } from "@ngx-translate/core";

import { LocaleService } from "src/app/shared/services/locale.service";
import { MeetdayModalFacade } from "../meetday-modal.facade";
import { DateTimePickerComponent } from "src/app/shared/components/datetime-picker/datetime-picker.component";
import { TagSelectorComponent } from "src/app/shared/components/tag-selector/tag-selector.component";
import { targetGroupOptions } from "src/app/shared/services/job.service";

@Component({
  selector: "app-meetday-tab-general",
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    DateTimePickerComponent,
    TagSelectorComponent,
  ],
  template: `
    <div [formGroup]="facade.meetDayForm">
      <div class="form-group">
        <label>{{ "Name" | translate }}*</label>
        <input
          class="form-control"
          formControlName="name"
          [class.is-invalid]="isFieldInvalid('name')"
        />
        @if (isFieldInvalid('name')) {
        <div class="invalid-feedback">
          @for (error of getFieldErrors('name'); track error) {
          <div>{{ error | translate }}</div>
          }
        </div>
        }
      </div>

      <div class="form-group">
        <label>{{ "Date" | translate }}*</label>
        <app-datetime-picker
          formControlName="date"
          mode="date"
          [isInvalid]="isFieldInvalid('date')"
        />
        @if (isFieldInvalid('date')) {
        <div class="invalid-feedback d-block">
          @for (error of getFieldErrors('date'); track error) {
          <div>{{ error | translate }}</div>
          }
        </div>
        }
      </div>

      <div class="form-group">
        <div class="custom-control custom-checkbox">
          <input
            class="custom-control-input"
            id="enableMaxStudents"
            type="checkbox"
            [checked]="facade.isMaxStudentsEnabled()"
            (change)="onMaxStudentsToggle($any($event.target).checked)"
          />
          <label class="custom-control-label" for="enableMaxStudents">
            {{ "Limit total student registrations" | translate }}
          </label>
        </div>
        <small class="form-text text-muted">
          {{
            "Enable to set a maximum number of student registrations for this meetday"
              | translate
          }}
        </small>
      </div>

      @if (facade.isMaxStudentsEnabled()) {
        <div class="form-group">
          <label>{{ "Maximum students" | translate }}*</label>
          <input
            type="number"
            min="1"
            class="form-control"
            formControlName="maximumAmountOfStudentsForMeetDay"
            [class.is-invalid]="
              isFieldInvalid('maximumAmountOfStudentsForMeetDay')
            "
          />
          @if (isFieldInvalid('maximumAmountOfStudentsForMeetDay')) {
          <div class="invalid-feedback">
            @for (error of getFieldErrors('maximumAmountOfStudentsForMeetDay');
            track error) {
            <div>{{ error | translate }}</div>
            }
          </div>
          }
        </div>
      }

      <h3>{{ "Address" | translate }}</h3>

      <div class="row" formGroupName="address">
        <div class="col-sm-8">
          <div class="form-group">
            <label>{{ "Street" | translate }}*</label>
            <input
              type="text"
              class="form-control"
              formControlName="street"
              [class.is-invalid]="isFieldInvalid('address.street')"
            />
            @if (isFieldInvalid('address.street')) {
            <div class="invalid-feedback">
              @for (error of getFieldErrors('address.street'); track error) {
              <div>{{ error | translate }}</div>
              }
            </div>
            }
          </div>
        </div>

        <div class="col-sm-2">
          <div class="form-group">
            <label>{{ "No." | translate }}*</label>
            <input
              type="text"
              class="form-control"
              formControlName="number"
              [class.is-invalid]="isFieldInvalid('address.number')"
            />
            @if (isFieldInvalid('address.number')) {
            <div class="invalid-feedback">
              @for (error of getFieldErrors('address.number'); track error) {
              <div>{{ error | translate }}</div>
              }
            </div>
            }
          </div>
        </div>

        <div class="col-sm-2">
          <div class="form-group">
            <label>{{ "Addition" | translate }}</label>
            <input
              type="text"
              class="form-control"
              formControlName="numberSuffix"
            />
          </div>
        </div>

        <div class="col-sm-8">
          <div class="form-group">
            <label>{{ "City" | translate }}*</label>
            <input
              type="text"
              class="form-control"
              formControlName="city"
              [class.is-invalid]="isFieldInvalid('address.city')"
            />
            @if (isFieldInvalid('address.city')) {
            <div class="invalid-feedback">
              @for (error of getFieldErrors('address.city'); track error) {
              <div>{{ error | translate }}</div>
              }
            </div>
            }
          </div>
        </div>

        <div class="col-sm-4">
          <div class="form-group">
            <label>{{ "Postal code" | translate }}*</label>
            <input
              type="text"
              class="form-control"
              formControlName="postalCode"
              [class.is-invalid]="isFieldInvalid('address.postalCode')"
            />
            @if (isFieldInvalid('address.postalCode')) {
            <div class="invalid-feedback">
              @for (error of getFieldErrors('address.postalCode'); track error)
              {
              <div>{{ error | translate }}</div>
              }
            </div>
            }
          </div>
        </div>

        <div class="col-sm-12">
          <div class="form-group">
            <label>{{ "Country" | translate }}*</label>
            <select
              class="form-control"
              formControlName="country"
              [class.is-invalid]="isFieldInvalid('address.country')"
            >
              @for (option of locale.availableCountries(); track option.code) {
              <option [value]="option.code">{{ option.name }}</option>
              }
            </select>
            @if (isFieldInvalid('address.country')) {
            <div class="invalid-feedback">
              @for (error of getFieldErrors('address.country'); track error) {
              <div>{{ error | translate }}</div>
              }
            </div>
            }
          </div>
        </div>
      </div>

      <h3>{{ "Meetday Type" | translate }}</h3>

      <div class="form-group">
        <label>{{ "Target groups" | translate }}</label>
        <app-tag-selector
          [options]="targetGroupOptions"
          formControlName="targetGroups"
        ></app-tag-selector>
      </div>

      <div class="form-group">
        <div class="custom-control custom-radio">
          <input
            class="custom-control-input"
            id="modeSimple"
            type="radio"
            [checked]="facade.meetdayMode() === 'simple'"
            (click)="onModeChange($event, 'simple')"
          />
          <label class="custom-control-label" for="modeSimple">
            {{ "Simple - Students select companies" | translate }}
          </label>
        </div>
        <small class="form-text text-muted ml-4">
          {{
            "Students select preferred companies during registration"
              | translate
          }}
        </small>
      </div>

      <div class="form-group">
        <div class="custom-control custom-radio">
          <input
            class="custom-control-input"
            id="modeRounds"
            type="radio"
            [checked]="facade.meetdayMode() === 'rounds'"
            (click)="onModeChange($event, 'rounds')"
          />
          <label class="custom-control-label" for="modeRounds">
            {{
              "With Rounds - Timed sessions with capacity limits" | translate
            }}
          </label>
        </div>
        <small class="form-text text-muted ml-4">
          {{
            "Define time rounds and limit students per company per round"
              | translate
          }}
        </small>
      </div>

      <!-- Simple mode options -->
      @if (facade.meetdayMode() === 'simple') {
      <div class="card card-body bg-light mt-3">
        <div class="form-group">
          <div class="custom-control custom-checkbox">
            <input
              class="custom-control-input"
              id="enableCompaniesLimit"
              type="checkbox"
              [checked]="isCompaniesLimitEnabled()"
              (change)="onCompaniesLimitToggle($any($event.target).checked)"
            />
            <label class="custom-control-label" for="enableCompaniesLimit">
              {{ "Limit companies per student" | translate }}
            </label>
          </div>
        </div>
        @if (isCompaniesLimitEnabled()) {
          <div class="form-group mb-0">
            <label>{{ "Maximum companies per student" | translate }}*</label>
            <input
              type="number"
              class="form-control"
              formControlName="companiesPerStudent"
              [class.is-invalid]="isFieldInvalid('companiesPerStudent')"
            />
            @if (isFieldInvalid('companiesPerStudent')) {
            <div class="invalid-feedback">
              @for (error of getFieldErrors('companiesPerStudent'); track error)
              {
              <div>{{ error | translate }}</div>
              }
            </div>
            }
          </div>
        }
      </div>
      }

      <!-- Rounds mode options -->
      @if (facade.meetdayMode() === 'rounds') {
      <div class="card card-body bg-light mt-3">
        <div class="form-group">
          <div class="custom-control custom-checkbox">
            <input
              class="custom-control-input"
              id="enableRoundLimit"
              type="checkbox"
              [checked]="isRoundLimitEnabled()"
              (change)="onRoundLimitToggle($any($event.target).checked)"
            />
            <label class="custom-control-label" for="enableRoundLimit">
              {{ "Limit students per company per round" | translate }}
            </label>
          </div>
          <small class="form-text text-muted">
            {{ "Override per company on the Companies tab." | translate }}
          </small>
        </div>
        @if (isRoundLimitEnabled()) {
          <div class="form-group mb-0">
            <label
              >{{ "Max students per company per round" | translate }}*</label
            >
            <input
              type="number"
              class="form-control"
              formControlName="studentsPerMeetdayRound"
              [class.is-invalid]="isFieldInvalid('studentsPerMeetdayRound')"
            />
            @if (isFieldInvalid('studentsPerMeetdayRound')) {
            <div class="invalid-feedback">
              @for (error of getFieldErrors('studentsPerMeetdayRound'); track
              error) {
              <div>{{ error | translate }}</div>
              }
            </div>
            }
          </div>
        }
      </div>
      }
    </div>
  `,
})
export class MeetdayTabGeneralComponent {
  locale = inject(LocaleService);
  facade = inject(MeetdayModalFacade);

  // Target group options for the tag selector
  targetGroupOptions = targetGroupOptions;

  onMaxStudentsToggle(checked: boolean): void {
    this.facade.meetDayForm
      .get("maximumAmountOfStudentsForMeetDay")
      ?.setValue(checked ? 1 : null);
  }

  /**
   * Handle mode change with confirmation for switching to simple mode.
   * Only prevents default when confirmation dialog is needed.
   */
  async onModeChange(event: Event, mode: "simple" | "rounds"): Promise<void> {
    const needsConfirmation =
      mode === "simple" && this.facade.roundsFormArray.length > 0;

    if (needsConfirmation) {
      event.preventDefault();
    }

    const success = await this.facade.setMeetdayMode(mode);

    // If confirmation was cancelled, revert the radio to its previous state
    if (needsConfirmation && !success) {
      (event.target as HTMLInputElement).checked = false;
    }
  }

  // Limit checkbox helpers
  isCompaniesLimitEnabled(): boolean {
    const value = this.facade.companiesPerStudentValue();
    return value !== null && value !== 0;
  }

  isRoundLimitEnabled(): boolean {
    const value = this.facade.studentsPerMeetdayRoundValue();
    return value !== null && value !== 0;
  }

  onCompaniesLimitToggle(checked: boolean): void {
    this.facade.meetDayForm
      .get("companiesPerStudent")
      ?.setValue(checked ? 1 : 0);
  }

  onRoundLimitToggle(checked: boolean): void {
    this.facade.meetDayForm
      .get("studentsPerMeetdayRound")
      ?.setValue(checked ? 1 : 0);
  }

  // Form Validation Helper Methods
  isFieldInvalid(fieldPath: string): boolean {
    const field = this.facade.meetDayForm.get(fieldPath);
    return !!(field && field.invalid && (field.dirty || field.touched));
  }

  getFieldErrors(fieldPath: string): string[] {
    const field = this.facade.meetDayForm.get(fieldPath);
    if (!field || !field.errors) {
      return [];
    }

    const errors: string[] = [];

    if (field.errors["required"]) {
      errors.push("This field is required");
    }
    if (field.errors["min"]) {
      errors.push(`Minimum value is ${field.errors["min"].min}`);
    }
    if (field.errors["max"]) {
      errors.push(`Maximum value is ${field.errors["max"].max}`);
    }

    return errors;
  }
}
